class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/*
 * Lululemon-style hover-intent mega menu.
 *
 * - Desktop pointer hover opens with a short intent delay and closes with a
 *   short grace delay (so the cursor can travel from the trigger into the
 *   panel without the menu disappearing).
 * - Moving between top-level items swaps the open panel instantly (no
 *   close/reopen flicker) because all instances share static open/close state.
 * - Touch and keyboard keep using the native <details> toggle, so mobile and
 *   accessibility are unaffected.
 * - The whole <header-menu> element (trigger + full-width panel, which is a
 *   DOM descendant) is the hover zone, so crossing the small gap between the
 *   link and the panel never closes it.
 */
class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');

    const parentLink = this.summary.querySelector('a[href]');
    if (parentLink) {
      parentLink.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        window.location.assign(parentLink.href);
      });
    }

    this.addEventListener('pointerenter', this.onPointerEnter.bind(this));
    this.addEventListener('pointerleave', this.onPointerLeave.bind(this));

    // Bind the nav-bar disengage handler per header wrapper (WeakSet-guarded)
    // instead of once globally, so a replaced wrapper (theme editor section
    // reload) gets its own listener instead of a stale one.
    const wrapper = this.closest('.header-wrapper');
    if (wrapper && !HeaderMenu._wrapperBound.has(wrapper)) {
      HeaderMenu._wrapperBound.add(wrapper);
      wrapper.addEventListener('pointerleave', HeaderMenu.onWrapperPointerLeave);
    }

    HeaderMenu.bindGlobalListeners();
  }

  static get isDesktop() {
    return window.matchMedia('(min-width: 990px)').matches;
  }

  onPointerEnter(event) {
    // Hover-intent is desktop + fine-pointer only; touch uses native tap.
    if (event.pointerType === 'touch' || !HeaderMenu.isDesktop) return;
    clearTimeout(HeaderMenu.closeTimer);

    if (HeaderMenu.openInstance && HeaderMenu.openInstance !== this) {
      // Another panel is already open — swap immediately, no reopen delay.
      this.openNow();
    } else if (!this.mainDetailsToggle.open) {
      clearTimeout(HeaderMenu.openTimer);
      if (HeaderMenu.OPEN_DELAY <= 0) {
        this.openNow();
      } else {
        HeaderMenu.openTimer = setTimeout(this.openNow.bind(this), HeaderMenu.OPEN_DELAY);
      }
    }
  }

  onPointerLeave(event) {
    if (event.pointerType === 'touch' || !HeaderMenu.isDesktop) return;
    clearTimeout(HeaderMenu.openTimer);
    clearTimeout(HeaderMenu.closeTimer);
    HeaderMenu.closeTimer = setTimeout(this.closeNow.bind(this), HeaderMenu.CLOSE_DELAY);
  }

  openNow() {
    clearTimeout(HeaderMenu.openTimer);
    if (HeaderMenu.openInstance && HeaderMenu.openInstance !== this) {
      HeaderMenu.openInstance.applyClosed();
    }
    this.applyOpen();
  }

  closeNow() {
    this.applyClosed();
  }

  // Called by DetailsDisclosure.onFocusOut when focus leaves the menu.
  close() {
    this.closeNow();
  }

  applyOpen() {
    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', 'true');
    this.classList.add('nav-item--active');
    this.content.classList.add('mega-menu-panel--active');
    document.documentElement.classList.add('mega-menu-is-open');
    HeaderMenu.engageNavBar();
    HeaderMenu.openInstance = this;
    HeaderMenu.openScrollY = window.scrollY;
  }

  applyClosed() {
    this.mainDetailsToggle.removeAttribute('open');
    this.summary.setAttribute('aria-expanded', 'false');
    this.classList.remove('nav-item--active');
    this.content.classList.remove('mega-menu-panel--active');
    if (HeaderMenu.openInstance === this) HeaderMenu.openInstance = null;
    if (!document.querySelector('header-menu.nav-item--active')) {
      document.documentElement.classList.remove('mega-menu-is-open');
    }
  }

  static engageNavBar() {
    document.documentElement.classList.add('header-nav-engaged');
  }

  static disengageNavBar() {
    document.documentElement.classList.remove('header-nav-engaged');
  }

  static onWrapperPointerLeave(event) {
    const wrapper = event.currentTarget;
    const related = event.relatedTarget;
    if (related && wrapper.contains(related)) return;
    HeaderMenu.disengageNavBar();
  }

  // Fires for ANY open/close cause (hover, keyboard Enter, click) so state
  // stays consistent and the sticky header is told to stay revealed.
  onToggle() {
    const isOpen = this.mainDetailsToggle.hasAttribute('open');
    this.summary.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    this.classList.toggle('nav-item--active', isOpen);
    this.content.classList.toggle('mega-menu-panel--active', isOpen);

    if (isOpen) {
      if (HeaderMenu.openInstance && HeaderMenu.openInstance !== this) {
        HeaderMenu.openInstance.applyClosed();
      }
      HeaderMenu.openInstance = this;
      HeaderMenu.openScrollY = window.scrollY;
      document.documentElement.classList.add('mega-menu-is-open');
      HeaderMenu.engageNavBar();
    } else if (HeaderMenu.openInstance === this) {
      HeaderMenu.openInstance = null;
      if (!document.querySelector('header-menu.nav-item--active')) {
        document.documentElement.classList.remove('mega-menu-is-open');
      }
    }

    if (!this.header) return;
    this.header.preventHide = HeaderMenu.openInstance != null;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }

  static bindGlobalListeners() {
    if (HeaderMenu._globalsBound) return;
    HeaderMenu._globalsBound = true;

    // Escape closes and returns focus to the trigger.
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !HeaderMenu.openInstance) return;
      const instance = HeaderMenu.openInstance;
      instance.closeNow();
      instance.summary.focus();
    });

    // Click outside the nav (logo, page, search, cart, account…) closes it.
    document.addEventListener('pointerdown', (event) => {
      if (HeaderMenu.openInstance && !event.target.closest('header-menu')) {
        HeaderMenu.openInstance.closeNow();
      }
    });

    // Focus moving outside the nav (e.g. into the search field) closes it.
    document.addEventListener('focusin', (event) => {
      if (HeaderMenu.openInstance && !event.target.closest('header-menu')) {
        HeaderMenu.openInstance.closeNow();
      }
    });

    // Meaningful scroll closes it (cheap, passive).
    window.addEventListener(
      'scroll',
      () => {
        if (
          HeaderMenu.openInstance &&
          Math.abs(window.scrollY - HeaderMenu.openScrollY) > HeaderMenu.SCROLL_CLOSE_THRESHOLD
        ) {
          HeaderMenu.openInstance.closeNow();
        }
      },
      { passive: true }
    );
  }
}

HeaderMenu.OPEN_DELAY = 0;
HeaderMenu.CLOSE_DELAY = 100;
HeaderMenu.SCROLL_CLOSE_THRESHOLD = 24;
HeaderMenu.openInstance = null;
HeaderMenu.openScrollY = 0;
HeaderMenu.openTimer = null;
HeaderMenu.closeTimer = null;
HeaderMenu._globalsBound = false;
HeaderMenu._wrapperBound = new WeakSet();

customElements.define('header-menu', HeaderMenu);
